<script>
import CourseLearnDetailComponent from '../../features/details/CourseLearnDetailComponent';
import EvaluationView from './EvaluationView';
import PdfViewerView from '../../../shared/pdfViewer/BoxView.vue';
import BrandLogo from '../../../shared/BrandLogo.vue';

export default {
  name: 'CourseLearnDetailView',
  components: {
        'brand-logo': BrandLogo,
    'evaluation-view': EvaluationView,
    PdfViewerView,
  },
  extends: CourseLearnDetailComponent,
};
</script>

<template>
  <div
    class="course_learn_page"
    :class="{ 'is-ready': !fullLoader }"
  >
    <!-- Estado de arranque: sustituye al layout mientras se carga el curso -->
    <div
      v-if="fullLoader"
      class="learn_bootstrap"
    >
      <div class="learn_bootstrap_card">
        <div class="bootstrap_brand">
          <brand-logo variant="light" />
          <span class="bootstrap_brand_tag">
            Aula Virtual
          </span>
        </div>

        <div class="bootstrap_orbit">
          <span class="orbit_ring orbit_ring_1" />
          <span class="orbit_ring orbit_ring_2" />
          <span class="orbit_core">
            <i class="uil uil-play" />
          </span>
        </div>

        <h2 class="bootstrap_title">
          Preparando tu aula virtual…
        </h2>
        <p class="bootstrap_desc">
          Estamos organizando el temario del curso y sincronizando tu avance.
        </p>

        <ul class="bootstrap_steps">
          <li>
            <span class="step_dot" /> Cargando el temario del curso
          </li>
          <li>
            <span class="step_dot" /> Sincronizando tu progreso
          </li>
          <li>
            <span class="step_dot" /> Preparando la lección activa
          </li>
        </ul>
      </div>
    </div>

    <!-- Top Learning Header -->
    <div class="course_learn_header">
      <div class="learn_header_left">
        <button
          class="learn_back_btn"
          title="Volver a la ficha del curso"
          @click="$router.push('/estudiante/cursos/' + ($route.params.id) + '/detalles')"
        >
          <i class="uil uil-arrow-left" />
          <span class="btn_text">Volver al Curso</span>
        </button>

        <div class="learn_header_divider" />

        <div class="learn_header_course_info">
          <h2
            class="learn_course_title"
            :title="courseDetail?.title"
          >
            {{ courseDetail?.title || 'Cargando curso...' }}
          </h2>
        </div>
      </div>

      <div class="learn_header_center">
        <span
          v-if="activitySelected"
          class="learn_active_lesson_pill"
          :title="activitySelected.title"
        >
          <i :class="showIconActivityType(activitySelected)" />
          <span class="lesson_pill_text">{{ activitySelected.title }}</span>
        </span>
      </div>

      <div class="learn_header_right">
        <div class="learn_progress_widget">
          <div class="progress_text_row">
            <span class="prog_label">Progreso</span>
            <span class="prog_percent">{{ courseProgressPercentage }}%</span>
          </div>
          <el-progress
            :percentage="courseProgressPercentage"
            :stroke-width="5"
            :show-text="false"
            color="#0D47A1"
            class="learn_progress_bar"
          />
        </div>

        <button
          class="curriculum_toggle_btn"
          :class="{ 'active': !isCurriculumCollapsed }"
          title="Alternar temario del curso"
          @click="isCurriculumCollapsed = !isCurriculumCollapsed"
        >
          <i class="uil uil-list-ui-alt" />
          <span class="btn_text">Temario</span>
        </button>
      </div>
    </div>

    <!-- Main Two-Column Classroom Layout -->
    <div
      class="course_learn_stage"
      :class="{ 'sidebar-collapsed': isCurriculumCollapsed }"
    >
      <!-- Left Curriculum Sidebar -->
      <aside class="curriculum_sidebar">
        <div class="curriculum_sidebar_header">
          <div class="sidebar_header_titles">
            <h4 class="curriculum_title">
              <i class="uil uil-books" />
              <span>Temario del Curso</span>
            </h4>
            <span class="activities_count_tag">
              {{ getCompletedActivitiesCount() }}/{{ getTotalActivitiesCount() }} completadas
            </span>
          </div>
        </div>

        <div
          v-if="courseDetail"
          class="curriculum_sections_list"
        >
          <el-collapse v-model="courseSectionActive">
            <el-collapse-item
              v-for="(section, sIdx) in orderBySort(courseDetail.courseSections)"
              :key="section.id || sIdx"
              :name="section.id"
              class="curriculum_section_item"
            >
              <template #title>
                <div class="section_header_row">
                  <div class="section_header_title">
                    <span class="section_icon_wrap">
                      <i class="uil uil-folder-open" />
                    </span>
                    <span
                      class="section_name_text"
                      :title="section.title"
                    >{{ section.title }}</span>
                  </div>
                  <span
                    v-if="section.totalHours"
                    class="section_hours_badge"
                  >
                    {{ section.totalHours }}h
                  </span>
                </div>
              </template>

              <!-- Subjects within section -->
              <div
                v-for="(subject, subIdx) in orderCourseSubjects(section.courseSubjects)"
                :key="subject.id || subIdx"
                class="curriculum_subject_block"
              >
                <div class="subject_block_title">
                  <i class="uil uil-bookmark" />
                  <span>{{ subject.title }}</span>
                </div>

                <!-- Activities within subject -->
                <div class="activities_list">
                  <div
                    v-for="(activity, aIdx) in orderCourseSubjectActivities(subject.courseSubjectActivities)"
                    :key="activity.id || aIdx"
                    class="curriculum_activity_row"
                    :class="{
                      'is_active': activitySelected && activitySelected.id === activity.id,
                      'is_completed': isActivityCompleted(activity.id)
                    }"
                    @click="onSelectActivity(activity)"
                  >
                    <button
                      type="button"
                      class="activity_check_box"
                      :class="{ 'checked': isActivityCompleted(activity.id) }"
                      title="Marcar como completada"
                      @click.stop="toggleActivityProgress(activity)"
                    >
                      <i
                        v-if="isActivityCompleted(activity.id)"
                        class="uil uil-check"
                      />
                    </button>

                    <div class="activity_type_icon">
                      <i :class="showIconActivityType(activity)" />
                    </div>

                    <span
                      class="activity_row_title"
                      :title="activity.title"
                    >
                      {{ activity.title }}
                    </span>

                    <span
                      v-if="activity.duration"
                      class="activity_duration_pill"
                    >
                      {{ activity.duration }}m
                    </span>
                  </div>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </aside>

      <!-- Right Classroom Stage -->
      <main class="classroom_main_content">
        <!-- El cambio de lección se comunica con un esqueleto de contenido
             (.classroom_content_skeleton), no con el spinner de Element Plus. -->
        <div
          class="classroom_content_inner"
        >
          <!-- Esqueleto de contenido: se usa al cambiar de lección -->
          <div
            v-if="contentLoader"
            class="classroom_content_skeleton"
          >
            <div class="skeleton_block skeleton_media" />
            <div class="skeleton_block">
              <span class="skeleton_line sk_title" />
              <span class="skeleton_line" />
              <span class="skeleton_line" />
              <span class="skeleton_line sk_short" />
            </div>
          </div>

          <!-- Welcome screen when no activity is selected -->
          <div
            v-if="!activitySelected"
            class="classroom_welcome_card"
          >
            <div class="welcome_icon_circle">
              <i class="uil uil-graduation-cap" />
            </div>
            <h2>¡Bienvenido a la clase!</h2>
            <p>Selecciona una lección del temario lateral para iniciar tu estudio o pulsa el botón a continuación:</p>
            <el-button
              type="primary"
              size="large"
              class="btn_start_first"
              @click="onStartFirstLecture"
            >
              <i class="uil uil-play" /> Comenzar primera lección
            </el-button>
          </div>

          <!-- Active Activity Classroom View -->
          <div
            v-else
            class="active_activity_view"
          >
            <!-- VIDEO PLAYER -->
            <div
              v-if="activitySelected.hasVideo"
              class="player_video_card"
            >
              <div class="video_aspect_box">
                <iframe
                  v-if="activitySelected.videoUrl"
                  :src="activitySelected.videoUrl"
                  frameborder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; unload"
                  allowfullscreen
                />
              </div>
            </div>

            <!-- PDF / DOCUMENT VIEWER -->
            <div
              v-if="activitySelected.hasFile"
              class="player_file_card"
            >
              <PdfViewerView
                v-if="activitySelected.fileUrl && currentPdfViewer"
                :file-url="activitySelected.fileUrl"
                :bind-context-menu="onBindFileContextMenu"
              />
            </div>

            <!-- Sticky Lesson Navigation Bar -->
            <div class="lesson_nav_dock">
              <button
                class="nav_step_btn prev"
                :disabled="!currentCourseNode || !currentCourseNode.prev"
                @click="onPrevLecture"
              >
                <i class="uil uil-arrow-left" />
                <span>Lección Anterior</span>
              </button>

              <button
                class="nav_complete_toggle_btn"
                :class="{ 'completed': isActivityCompleted(activitySelected.id) }"
                @click="toggleActivityProgress(activitySelected)"
              >
                <i :class="isActivityCompleted(activitySelected.id) ? 'uil uil-check-circle' : 'uil uil-circle'" />
                <span>{{ isActivityCompleted(activitySelected.id) ? 'Lección Completada' : 'Marcar como Completada' }}</span>
              </button>

              <button
                class="nav_step_btn next"
                @click="onNextLecture"
              >
                <span>{{ (currentCourseNode && currentCourseNode.next) ? 'Siguiente Lección' : 'Finalizar Curso' }}</span>
                <i class="uil uil-arrow-right" />
              </button>
            </div>

            <!-- Lesson Info Card -->
            <div class="lesson_info_card">
              <div class="lesson_info_header">
                <span class="lesson_type_chip">
                  <i :class="showIconActivityType(activitySelected)" />
                  {{ activitySelected.courseSubjectActivityType === 2 ? 'Examen / Test' : 'Lección de Estudio' }}
                </span>
                <h1 class="lesson_title">
                  {{ activitySelected.title }}
                </h1>
              </div>

              <!-- Rich description/text -->
              <div
                v-if="activitySelected.description"
                class="lesson_html_body"
                v-html="activitySelected.description"
              />

              <!-- Multiple Choice Activity -->
              <div
                v-if="activitySelected.hasMultipleResponse"
                class="activity_interactive_box multiple_choice_box"
              >
                <h3 class="interactive_title">
                  <i class="uil uil-check-square" /> Selecciona la respuesta correcta:
                </h3>
                <el-checkbox-group
                  v-model="activityAnswers"
                  class="choice_options_group"
                >
                  <div
                    v-for="answer in activitySelected.courseSubjectActivityAnswers"
                    :key="answer.id"
                    class="choice_option_card"
                  >
                    <el-checkbox :value="answer.id">
                      <div
                        class="choice_text"
                        v-html="answer.title"
                      />
                    </el-checkbox>
                  </div>
                </el-checkbox-group>

                <div class="interactive_actions">
                  <el-button
                    type="primary"
                    size="large"
                    class="btn_submit_answer"
                    @click="onSendMultipleAnswer"
                  >
                    <i class="uil uil-message" /> Enviar Respuesta
                  </el-button>
                </div>
              </div>

              <!-- Open Question Activity -->
              <div
                v-if="activitySelected.hasOpenResponse"
                class="activity_interactive_box open_answer_box"
              >
                <h3 class="interactive_title">
                  <i class="uil uil-edit" /> Tu Respuesta:
                </h3>
                <el-input
                  v-model="openAnswer"
                  type="textarea"
                  :rows="4"
                  placeholder="Escribe tu respuesta analítica aquí..."
                  class="open-answer-editor"
                />
                <div class="interactive_actions">
                  <el-button
                    type="primary"
                    size="large"
                    class="btn_submit_answer"
                    @click="onSendOpenAnswer"
                  >
                    <i class="uil uil-message" /> Enviar Respuesta
                  </el-button>
                </div>
              </div>

              <!-- Test / Exam Activity (activityTypeSelected === 2) -->
              <div
                v-if="activityTypeSelected === 2"
                class="activity_interactive_box test_exam_box"
              >
                <!-- Pre-Test Start Card (before beginning test or if paused) -->
                <div
                  v-if="!isDoingTest && !showTestResult"
                  class="test_start_card"
                >
                  <div class="test_start_header">
                    <div class="test_badge">
                      <i class="uil uil-award" />
                      <span>Evaluación de Conocimientos</span>
                    </div>
                    <h3 class="test_start_title">
                      {{ activitySelected.title }}
                    </h3>
                    <div
                      v-if="activitySelected.description"
                      class="test_start_desc"
                      v-html="activitySelected.description"
                    />
                    <p
                      v-else
                      class="test_start_desc"
                    >
                      Esta lección consiste en un examen evaluativo. Asegúrate de contar con tiempo suficiente y una conexión estable antes de iniciar.
                    </p>
                  </div>

                  <div class="test_meta_grid">
                    <div
                      v-if="activitySelected.courseSubjectTestActivities && activitySelected.courseSubjectTestActivities.length"
                      class="test_meta_item"
                    >
                      <i class="uil uil-layer-group" />
                      <div>
                        <span class="meta_label">Secciones</span>
                        <span class="meta_val">{{ activitySelected.courseSubjectTestActivities.length }} sección(es)</span>
                      </div>
                    </div>
                    <div
                      v-if="activitySelected.duration"
                      class="test_meta_item"
                    >
                      <i class="uil uil-stopwatch" />
                      <div>
                        <span class="meta_label">Duración</span>
                        <span class="meta_val">{{ activitySelected.duration }} minutos</span>
                      </div>
                    </div>
                    <div
                      v-if="testTryNumber !== null && testTryNumber !== undefined"
                      class="test_meta_item"
                    >
                      <i class="uil uil-history" />
                      <div>
                        <span class="meta_label">Intentos previas</span>
                        <span class="meta_val">{{ testTryNumber }} intento(s)</span>
                      </div>
                    </div>
                  </div>

                  <div class="test_start_action">
                    <el-button
                      type="primary"
                      size="large"
                      class="btn_start_exam"
                      @click="onSelectTestActivity"
                    >
                      <i class="uil uil-play" /> Comenzar Examen
                    </el-button>
                  </div>
                </div>

                <!-- Active test in progress -->
                <div
                  v-if="isDoingTest && !showTestResult && testSectionSelected"
                  class="test_active_panel"
                >
                  <div class="test_header_row">
                    <div>
                      <h3
                        class="test_name"
                        v-html="activitySelected.title"
                      />
                      <h4
                        class="test_sec_desc"
                        v-html="testSectionSelected.value.description"
                      />
                    </div>
                    <div
                      v-if="timerLabel"
                      class="test_countdown_pill"
                    >
                      <i class="uil uil-clock" />
                      <span>{{ timerLabel }}</span>
                    </div>
                  </div>

                  <div
                    v-if="testSectionSelected.value.instruction"
                    class="test_instructions_box"
                  >
                    <h5><i class="uil uil-info-circle" /> Instrucciones:</h5>
                    <div v-html="testSectionSelected.value.instruction" />
                  </div>

                  <div
                    v-if="testSectionSelected.value.fileUrl"
                    class="test_file_box"
                  >
                    <iframe
                      id="file-wrapper-viewer"
                      :src="getPdfViewer(testSectionSelected.value)"
                      @load="onBindFileContextMenu"
                    />
                  </div>

                  <!-- Questions list -->
                  <div
                    v-if="hasQuestions(testSectionSelected.value.courseSubjectTestQuestions)"
                    class="test_questions_container"
                  >
                    <div
                      v-for="(question, qIdx) in filterQuestionsByAnswers(testSectionSelected.value.courseSubjectTestQuestions)"
                      :key="question.id"
                      class="test_question_card"
                    >
                      <div class="question_header">
                        <span class="question_number">Pregunta {{ qIdx + 1 }}</span>
                        <div
                          class="question_text"
                          v-html="question.description"
                        />
                      </div>

                      <el-checkbox-group
                        v-if="isCollection(question.answers)"
                        v-model="question.answers"
                        class="test_answers_group"
                        @change="onChangeTestAnswer"
                      >
                        <div
                          v-for="answer in question.courseSubjectTestQuestionAnswers"
                          :key="answer.id"
                          class="test_answer_pill"
                        >
                          <el-checkbox :value="answer.id">
                            <div v-html="answer.description" />
                          </el-checkbox>
                        </div>
                      </el-checkbox-group>
                    </div>

                    <!-- Test Actions Bar -->
                    <div class="test_actions_bar">
                      <el-button
                        v-if="testSectionSelected.prev"
                        @click="onPrevTestSection"
                      >
                        <i class="uil uil-arrow-left" /> Sección Anterior
                      </el-button>
                      <el-button
                        type="primary"
                        @click="onNextTestSection"
                      >
                        <span>{{ testSectionSelected.next ? 'Siguiente Sección' : 'Finalizar Examen' }}</span>
                        <i class="uil uil-arrow-right" />
                      </el-button>
                    </div>
                  </div>
                </div>

                <!-- Test Results Panel -->
                <div
                  v-if="showTestResult"
                  class="test_results_panel"
                >
                  <div
                    class="test_score_banner"
                    :class="isScoreTestSuccess() ? 'score_success' : 'score_failed'"
                  >
                    <div class="score_icon">
                      <i :class="isScoreTestSuccess() ? 'uil uil-check-circle' : 'uil uil-times-circle'" />
                    </div>
                    <div class="score_text">
                      <h2>Calificación: {{ getTestScore() }}</h2>
                      <span class="score_subtitle">
                        {{ isScoreTestSuccess() ? '¡Felicidades! Has aprobado este examen.' : 'No has alcanzado la calificación aprobatoria.' }}
                      </span>
                      <span class="score_tag">{{ getTestTotalSuccess() }} aciertos</span>
                    </div>
                  </div>

                  <!-- Questions breakdown -->
                  <div class="test_review_container">
                    <h4 class="review_title">
                      <i class="uil uil-clipboard-notes" /> Revisión de Respuestas
                    </h4>
                    <div
                      v-for="testActivity in activitySelected.courseSubjectTestActivities"
                      :key="testActivity.id"
                      class="test_activity_review_card"
                    >
                      <div
                        v-for="question in testActivity.courseSubjectTestQuestions"
                        :key="question.id"
                        class="review_question_item"
                      >
                        <div class="review_question_text">
                          <i class="uil uil-book-open" />
                          <span>{{ question.description }}</span>
                        </div>
                        <ul class="review_answers_list">
                          <li
                            v-for="answer in question.courseSubjectTestQuestionAnswers"
                            :key="answer.id"
                            :class="getStyleAnswerResponse(answer, question)"
                          >
                            <span class="answer_indicator">
                              <i
                                v-if="isAnswerResponse(answer) && answer.success && doScore(question)"
                                class="uil uil-check-circle text-success"
                              />
                              <i
                                v-else-if="isAnswerResponse(answer) && !answer.success"
                                class="uil uil-times-circle text-danger"
                              />
                              <i
                                v-else-if="answer.success"
                                class="uil uil-check text-muted"
                              />
                              <i
                                v-else
                                class="uil uil-circle text-muted"
                              />
                            </span>
                            <span
                              class="answer_text"
                              v-html="answer.description"
                            />
                            <span
                              v-if="answer.success"
                              class="correct_tag"
                            >Correcta</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- General Course Info -->
            <div
              v-if="courseDetail && courseDetail.about"
              class="course_general_info_card"
            >
              <div class="card_header">
                <i class="uil uil-info-circle" />
                <h3>Información General del Curso</h3>
              </div>
              <div
                class="card_body"
                v-html="courseDetail.about"
              />
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Modals -->
    <evaluation-view
      v-if="modalEvaluation.isVisible"
      :form-data="evaluationItem"
      :conf="modalEvaluation"
    />

    <!-- La carga inicial se resuelve con el estado de arranque (.learn_bootstrap);
         el loader fullscreen queda fuera por no duplicar la señal visual. -->
  </div>
</template>
